<template>
  <div :class="['forgot-password-container', { 'dark-theme': isDarkMode }]">
    <!-- Dark/Light Mode Toggle -->
    <div class="theme-toggle">
      <button @click="toggleTheme" class="theme-toggle-btn" :title="isDarkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'">
        <i :class="isDarkMode ? 'bi bi-sun-fill' : 'bi bi-moon-fill'"></i>
      </button>
    </div>

    <div class="d-flex flex-column min-vh-100">
      <!-- Header -->
      <header class="header-section border-bottom">
        <div class="container-fluid">
          <div class="d-flex align-items-center py-3 px-4">
            <div class="d-flex align-items-center justify-content-center">
              <img src="/logo_b.png" alt="SICECOM" class="forgot-logo" />
            </div>
          </div>
        </div>
      </header>

      <!-- Main Content -->
      <main class="flex-grow-1 d-flex align-items-center justify-content-center py-5">
        <div class="container">
          <div class="row justify-content-center">
            <div class="col-12 col-md-6 col-lg-5 col-xl-4">
              
              <!-- Recovery Form -->
              <div v-if="!emailSent" class="recovery-card">
                <div class="text-center mb-4">
                  <h2 class="recovery-title">¿Olvidaste tu contraseña?</h2>
                  <p class="recovery-subtitle">No te preocupes. Ingresa tu correo electrónico y te enviaremos un enlace para recuperarla.</p>
                </div>

                <form @submit.prevent="onSubmit" class="recovery-form">
                  <div class="mb-3">
                    <label for="ruc" class="visually-hidden">RUC</label>
                    <input
                      id="ruc"
                      v-model.trim="ruc"
                      type="text"
                      class="form-control recovery-input"
                      :class="{ 'is-invalid': error }"
                      placeholder="RUC de la empresa"
                      maxlength="13"
                      inputmode="numeric"
                      required
                      :disabled="loading"
                    />
                  </div>

                  <div class="mb-4">
                    <label for="email" class="visually-hidden">Correo electrónico</label>
                    <input
                      id="email"
                      v-model.trim="email"
                      type="email"
                      class="form-control recovery-input"
                      :class="{ 'is-invalid': error }"
                      placeholder="Correo electrónico"
                      required
                      autocomplete="email"
                      :disabled="loading"
                    />
                  </div>

                  <!-- Error Alert -->
                  <div v-if="error" class="alert alert-danger d-flex align-items-center mb-4" role="alert">
                    <i class="bi bi-exclamation-triangle-fill me-2"></i>
                    <div>{{ error }}</div>
                  </div>

                  <!-- Recovery Button -->
                  <button
                    type="submit"
                    class="btn recovery-btn w-100 py-3"
                    :disabled="loading"
                  >
                    <div v-if="loading" class="d-flex align-items-center justify-content-center">
                      <div class="spinner-border spinner-border-sm me-2" role="status">
                        <span class="visually-hidden">Enviando...</span>
                      </div>
                      Enviando enlace...
                    </div>
                    <span v-else>Enviar enlace de recuperación</span>
                  </button>
                </form>
              </div>

              <!-- Confirmation Message -->
              <div v-else class="confirmation-card">
                <div class="text-center">
                  <div class="success-icon mb-4">
                    <i class="bi bi-check-circle-fill"></i>
                  </div>
                  <h2 class="confirmation-title mb-3">¡Correo enviado!</h2>
                  <p class="confirmation-subtitle mb-4">
                    Hemos enviado un enlace para restablecer tu contraseña a tu correo electrónico. 
                    Por favor, revisa tu bandeja de entrada (y la carpeta de spam).
                  </p>
                  <button
                    @click="goToLogin"
                    class="btn back-to-iniciarSesion-btn w-100 py-3"
                  >
                    Volver a inicio de sesión
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import auth from '@/services/servicioAutenticacion';

export default {
  name: 'RecuperarClaveView',
  data() {
    return {
      ruc: '',
      email: '',
      loading: false,
      error: null,
      emailSent: false,
      isDarkMode: false
    };
  },
  mounted() {
    // Cargar preferencia de tema desde localStorage
    const savedTheme = localStorage.getItem('sicecom-client-theme');
    if (savedTheme) {
      this.isDarkMode = savedTheme === 'dark';
    } else {
      // Detectar preferencia del sistema
      this.isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    this.applyTheme();
  },
  methods: {
    toggleTheme() {
      this.isDarkMode = !this.isDarkMode;
      this.applyTheme();
      // Guardar preferencia en localStorage
      localStorage.setItem('sicecom-client-theme', this.isDarkMode ? 'dark' : 'light');
    },
    applyTheme() {
      // Aplicar clase al body para efectos globales si es necesario
      if (this.isDarkMode) {
        document.body.classList.add('dark-theme');
      } else {
        document.body.classList.remove('dark-theme');
      }
    },
    async onSubmit() {
      this.error = null;
      this.loading = true;
      
      try {
        const rucNormalizado = String(this.ruc || '').replace(/\D+/g, '');
        if (rucNormalizado.length !== 13) {
          throw new Error('El RUC debe tener 13 dígitos');
        }

        await auth.solicitarRecuperacion(rucNormalizado, this.email);
        this.emailSent = true;
      } catch (e) {
        // Mensajes claros por estado HTTP común
        const status = e?.response?.status;
        let msg = e?.response?.data?.error || e?.message || 'No se pudo enviar el enlace de recuperación';
        if (status === 404) msg = 'No se encontró una cuenta con este correo electrónico';
        if (status === 500) msg = 'Error del servidor. Intente nuevamente';
        this.error = msg;
      } finally {
        this.loading = false;
      }
    },
    goToLogin() {
      this.$router.push('/iniciar-sesion');
    }
  },
  beforeUnmount() {
    // Limpiar clase del body al salir del componente
    document.body.classList.remove('dark-theme');
  }
};
</script>

<style>
/* Variables CSS para temas */
.forgot-password-container {
  --primary-color: #1173d4;
  --primary-hover: #0f5fa3;
  --bg-light: #f6f7f8;
  --bg-dark: #101922;
  --text-primary: #0f172a;
  --text-secondary: #64748b;
  --border-color: #e2e8f0;
  --card-bg: #ffffff;
  --card-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  --header-border: #e2e8f0;
  --success-color: #10b981;
  --success-bg: #dcfce7;
  
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  background: var(--bg-light);
  color: var(--text-primary);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Tema oscuro */
.forgot-password-container.dark-theme {
  --bg-light: #101922;
  --bg-dark: #0f172a;
  --text-primary: #f1f5f9;
  --text-secondary: #94a3b8;
  --border-color: #374151;
  --card-bg: #1a242d;
  --card-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  --header-border: #374151;
  --success-color: #34d399;
  --success-bg: rgba(16, 185, 129, 0.15);
}

/* Toggle de tema */
.theme-toggle {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
}

.theme-toggle-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: var(--card-bg);
  color: var(--text-primary);
  box-shadow: var(--card-shadow);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.theme-toggle-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Header */
.header-section {
  background: var(--card-bg);
  border-color: var(--header-border) !important;
  backdrop-filter: blur(10px);
}

.forgot-logo {
  width: 250px;
  height: 75px;
  object-fit: contain;
}



.logo-text {
  color: var(--text-primary) !important;
  font-weight: 700 !important;
  letter-spacing: -0.025em;
}

/* Contenedor principal */
.forgot-password-container {
  min-height: 100vh;
  background: var(--bg-light);
}

/* Degradado solo en tema oscuro */
.forgot-password-container.dark-theme {
  background: linear-gradient(135deg, var(--bg-light) 0%, var(--bg-dark) 100%);
}

/* Tarjetas */
.recovery-card,
.confirmation-card {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: var(--card-shadow);
  border: 1px solid var(--border-color);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.recovery-card:hover,
.confirmation-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 32px 64px -12px rgba(0, 0, 0, 0.25);
}

/* Títulos */
.recovery-title,
.confirmation-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.025em;
}

.recovery-subtitle,
.confirmation-subtitle {
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
}

/* Formulario */
.recovery-form {
  margin-top: 2rem;
}

.recovery-input {
  background: var(--card-bg) !important;
  border: 2px solid var(--border-color) !important;
  color: var(--text-primary) !important;
  font-size: 0.875rem;
  padding: 0.875rem 1rem !important;
  border-radius: 8px !important;
  transition: all 0.3s ease !important;
}

.recovery-input:focus {
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 0.2rem rgba(17, 115, 212, 0.25) !important;
  background: var(--card-bg) !important;
}

.recovery-input::placeholder {
  color: var(--text-secondary) !important;
}

/* Botones */
.recovery-btn,
.back-to-iniciarSesion-btn {
  background: linear-gradient(135deg, var(--primary-color), #3b82f6) !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  letter-spacing: 0.025em !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 14px 0 rgba(17, 115, 212, 0.39) !important;
  color: white !important;
}

.recovery-btn:hover:not(:disabled),
.back-to-iniciarSesion-btn:hover:not(:disabled) {
  transform: translateY(-1px) !important;
  box-shadow: 0 8px 25px 0 rgba(17, 115, 212, 0.5) !important;
  background: linear-gradient(135deg, var(--primary-hover), #2563eb) !important;
}

.recovery-btn:disabled {
  opacity: 0.7 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

/* Icono de éxito */
.success-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto;
  background: var(--success-bg);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--success-color);
}

/* Alertas */
.alert-danger {
  background: rgba(239, 68, 68, 0.1) !important;
  border: 1px solid rgba(239, 68, 68, 0.3) !important;
  color: #dc2626 !important;
  border-radius: 8px !important;
  font-size: 0.875rem !important;
}

.dark-theme .alert-danger {
  background: rgba(239, 68, 68, 0.15) !important;
  border-color: rgba(239, 68, 68, 0.4) !important;
  color: #fca5a5 !important;
}

/* Spinner personalizado */
.spinner-border-sm {
  width: 1rem !important;
  height: 1rem !important;
}

/* Animaciones suaves */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.recovery-card,
.confirmation-card {
  animation: fadeInUp 0.6s ease-out;
}

/* Animación de transición entre estados */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.confirmation-card {
  animation: slideIn 0.5s ease-out;
}

/* Responsive */
@media (max-width: 768px) {
  .recovery-card,
  .confirmation-card {
    padding: 2rem 1.5rem;
    margin: 1rem;
  }

  .recovery-title,
  .confirmation-title {
    font-size: 1.75rem;
  }

  .theme-toggle {
    top: 15px;
    right: 15px;
  }

  .theme-toggle-btn {
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
  }
}

/* Mejoras de accesibilidad */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Focus visible para navegación por teclado */
.theme-toggle-btn:focus-visible,
.recovery-btn:focus-visible,
.back-to-iniciarSesion-btn:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}
</style>


